import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import {
  AuthService,
  LanguageService,
  PermissionService,
} from '@educational-initiatives/angular-web-utilities';
import type { AvailableLanguage } from '@app/core';
import { AuthFlowService } from '@app/core/services/auth-flow/auth-flow.service';
import { ReauthGateService } from '@app/core/services/reauth-gate/reauth-gate.service';
import { PosthogService } from '@app/core/services/posthog/posthog.service';
import { HeaderComponent } from '../header/header';
import { SidebarComponent } from '../sidebar/sidebar';
import { MobileBottomNavComponent } from '../mobile-bottom-nav/mobile-bottom-nav';
import { LoadingSpinnerComponent } from '@app/shared';
import { BlackScreenOverlayComponent } from '@app/shared/components/black-screen-overlay/black-screen-overlay.component';
import { ReauthModalComponent } from '@app/shared/components/reauth-modal/reauth-modal.component';
import type { NavItem } from '@app/shared';
import {
  DEFAULT_LAYOUT_VALUES,
  LOCALE_PATH_PATTERN,
  PERMISSION_NAME_SHOW_MANAGE_ACCOUNTS,
  PERMISSION_NAME_SHOW_SIDE_BAR,
} from '@app/shared';
import { MOBILE_NAV_ITEMS_RECORD } from '@app/shared/constants';
import { parseEnabledPermissionsFromBitwise } from '@core/utils/permission-bitwise.utils';

/**
 * @fileoverview main-layout.ts
 * @description
 * **The Main Stage**
 * The primary layout component that wraps all feature pages. It provides
 * the header, sidebar, main content, mobile bottom nav, and footer.
 * Manages sidebar open state and coordinates navigation. Sidebar and mobile
 * bottom nav use `show_side_bar`; the profile "Manage your account" link uses
 * `show_manage_accounts` (both from bitwise-decoded user permission hex +
 * PermissionService.getPermissions()). When `show_side_bar` is off, the
 * bottom nav is not rendered.
 *
 * ARCHITECTURAL PRINCIPLES:
 * 1. Layout composition
 * 2. Sidebar orchestration (overlay on mobile, push on desktop)
 * 3. Automatic locale detection
 * 4. Re-validate session when the tab becomes visible again (multi-tab logout)
 */
@Component({
  selector: 'app-main-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TranslocoModule,
    HeaderComponent,
    SidebarComponent,
    MobileBottomNavComponent,
    LoadingSpinnerComponent,
    BlackScreenOverlayComponent,
    ReauthModalComponent,
  ],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.scss',
})
export class MainLayoutComponent {
  private readonly _languageService = inject(LanguageService);
  private readonly _authService = inject(AuthService);
  private readonly _authFlow = inject(AuthFlowService);
  protected readonly _reauth = inject(ReauthGateService);
  private readonly _posthog = inject(PosthogService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _destroyRef = inject(DestroyRef);

  public readonly appName = input(DEFAULT_LAYOUT_VALUES.APP_NAME);
  public readonly companyName = input(DEFAULT_LAYOUT_VALUES.COMPANY_NAME);
  public readonly navItems = input<NavItem[]>([]);

  /**
   * When true, sidebar pushes content on mobile (header, main, footer shift).
   * When false (default), sidebar overlays on mobile without shifting content.
   */
  public readonly sidebarPushOnMobile = input(false);

  /** Sidebar open state */
  protected readonly _isSidebarOpen = signal(false);

  /**
   * Gated by bitwise-decoded `show_side_bar`. False until permission metadata
   * and user `permission` hex are available, or when the bit is off.
   */
  protected readonly _canShowSidebar = signal(false);

  /**
   * Gated by bitwise-decoded `show_manage_accounts`. False until permission metadata
   * and user `permission` hex are available, or when the bit is off.
   */
  protected readonly _canShowManageAccounts = signal(false);

  /**
   * @description
   * **Layout Bootstrapper**
   * Extracts locale from the base href and initializes the language service.
   */
  constructor() {
    effect(() => {
      const userInfo = this._authService.userInfo();
      if (userInfo?.user_id) {
        this._posthog.identify(userInfo.user_id, {
          email: userInfo.email,
          name: this._authService.getUserFullName(),
        });
      }
    });

    effect(() => {
      if (!this._canShowSidebar()) {
        this._isSidebarOpen.set(false);
      }
    });

    this._applyLayoutPermissionGates();

    effect(() => {
      const userId = this._authService.userInfo()?.user_id;
      if (userId) {
        this._applyLayoutPermissionGates();
      }
    });

    if (typeof document !== 'undefined') {
      const baseElement = document.querySelector('base');
      const baseHref = baseElement?.getAttribute('href') || '/';
      const localeMatch = baseHref.match(LOCALE_PATH_PATTERN);

      if (localeMatch?.[1]) {
        const extractedLocale = localeMatch[1] as AvailableLanguage;

        if (this._languageService.isValidLanguage(extractedLocale)) {
          this._languageService.setLanguage(extractedLocale);
        } else {
          const baseLang = (extractedLocale as string).split('-')[0];
          const availableLangs = this._languageService.availableLanguages;

          const matchingLocale = availableLangs.find(
            lang => lang === extractedLocale || lang.startsWith(`${baseLang}-`),
          ) as AvailableLanguage | undefined;

          if (matchingLocale) {
            this._languageService.setLanguage(matchingLocale);
          }
        }
      }

      const onVisibilityChange = (): void => {
        if (
          document.visibilityState !== 'visible' ||
          this._reauth.isRequired()
        ) {
          return;
        }
        void this._authFlow.revalidateSessionOnTabVisible();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      this._destroyRef.onDestroy(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      });
    }
  }

  protected readonly _navigationItems = computed(() => {
    const customItems = this.navItems();
    const canShowManage = this._canShowManageAccounts();

    let items: NavItem[];
    if (customItems && customItems.length > 0) {
      items = customItems;
    } else {
      items = Object.values(MOBILE_NAV_ITEMS_RECORD);
    }

    if (!canShowManage) {
      return items.filter(item => item.path !== '/manage-account');
    }
    return items;
  });

  /**
   * Nav items for mobile bottom nav: More, Manage, Home (center), Security, Subscriptions.
   * Uses pathOrder for default items; passes custom items through as-is.
   */
  protected readonly _mobileBottomNavItems = computed(() => {
    const items = this._navigationItems();
    const customItems = this.navItems();
    if (customItems && customItems.length > 0) {
      return items;
    }
    const pathOrder = ['/manage-account', '/home', 'security', 'payments'];
    return pathOrder
      .map(path => items.find(item => item.path === path))
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== undefined && item !== null,
      );
  });

  protected _openSidebar(): void {
    this._isSidebarOpen.set(true);
  }

  protected _toggleSidebar(): void {
    this._isSidebarOpen.update(isOpen => !isOpen);
  }

  protected _closeSidebar(): void {
    this._isSidebarOpen.set(false);
  }

  /**
   * Loads permission list/sequence from cache, decodes `AuthService.userInfo().permission`
   * with the bitwise engine, and sets layout gates (`show_side_bar`, `show_manage_accounts`).
   */
  private _applyLayoutPermissionGates(): void {
    void this._permissionService
      .getPermissions()
      .then(cached => {
        const user = this._authService.userInfo();
        const parsed = parseEnabledPermissionsFromBitwise(
          {
            permissions: cached?.permissions ?? [],
            permission_seq: cached?.permission_seq ?? [],
          },
          user?.permission ?? null,
        );
        this._canShowSidebar.set(
          parsed.some(
            p =>
              p.permissionName === PERMISSION_NAME_SHOW_SIDE_BAR && p.isEnabled,
          ),
        );
        this._canShowManageAccounts.set(
          parsed.some(
            p =>
              p.permissionName === PERMISSION_NAME_SHOW_MANAGE_ACCOUNTS &&
              p.isEnabled,
          ),
        );
      })
      .catch(() => {
        this._canShowSidebar.set(false);
        this._canShowManageAccounts.set(false);
      });
  }
}
